
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flashcard } from '../types';
import { generateFlashcards } from '../services/geminiService';
import Button from '../components/Button';

const FlashcardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [cards, setCards] = useState<Flashcard[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [topic, setTopic] = useState(searchParams.get('topic') || '');
  
  // Cooldown
  const [cooldown, setCooldown] = useState(0);
  
  // Params from URL or defaults
  const grade = searchParams.get('grade') || '4';
  const state = searchParams.get('state') || 'TX';

  useEffect(() => {
    if (topic && cards.length === 0) {
      loadCards();
    } else if (!topic) {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => {
     let timer: any;
     if (cooldown > 0) {
         timer = setInterval(() => setCooldown(c => c - 1), 1000);
     }
     return () => clearInterval(timer);
  }, [cooldown]);

  const loadCards = async () => {
    setLoading(true);
    setCooldown(10); // 10s cooldown
    const data = await generateFlashcards(grade, state, topic);
    setCards(data);
    setLoading(false);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleNext = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % cards.length);
    }, 200);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setTimeout(() => {
        setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    }, 200);
  };

  const handleStart = () => {
    if(topic && cooldown === 0) {
        navigate(`/flashcards?topic=${topic}&grade=${grade}&state=${state}`, { replace: true });
        loadCards();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
         <div className="w-16 h-16 border-4 border-brand-accent border-t-transparent rounded-full animate-spin mb-4"></div>
         <h2 className="text-xl font-bold text-slate-600 animate-bounce">Creating Flashcards...</h2>
      </div>
    );
  }

  // Input State
  if (cards.length === 0) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-12">
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 text-center">
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl text-brand-blue">
                    <i className="fas fa-layer-group"></i>
                </div>
                <h2 className="text-3xl font-bold text-indigo-900 mb-4">Flashcard Study Mode</h2>
                <p className="text-slate-500 mb-8">
                    Enter a topic to generate a custom set of flashcards based on the {grade}th Grade {state} curriculum.
                </p>
                
                <div className="max-w-md mx-auto space-y-4">
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="e.g. State Capitals, Multiplication, The Water Cycle"
                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                    />
                    <Button onClick={handleStart} disabled={!topic || cooldown > 0} className="w-full justify-center">
                        {cooldown > 0 ? `Wait ${cooldown}s` : 'Create Deck'}
                    </Button>
                </div>
            </div>
            <div className="mt-8 text-center">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
            </div>
        </div>
      )
  }

  const currentCard = cards[currentIndex];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left mr-2"></i> Dashboard
        </Button>
        <span className="font-bold text-slate-500">
            Card {currentIndex + 1} of {cards.length}
        </span>
      </div>

      <div className="perspective-1000 w-full max-w-xl mx-auto h-80 cursor-pointer group" onClick={() => setIsFlipped(!isFlipped)}>
        <div className={`relative w-full h-full transition-all duration-500 [transform-style:preserve-3d] ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`}>
            
            {/* Front */}
            <div className="absolute w-full h-full backface-hidden [backface-visibility:hidden]">
                <div className="w-full h-full bg-white rounded-3xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border-2 border-slate-100 flex flex-col items-center justify-center p-8 text-center hover:border-brand-blue transition-colors">
                    <span className="text-sm font-bold text-brand-blue uppercase tracking-widest mb-4">Term</span>
                    <h3 className="text-3xl font-bold text-slate-800">{currentCard.front}</h3>
                    <p className="absolute bottom-6 text-slate-400 text-sm">Tap to flip</p>
                </div>
            </div>

            {/* Back */}
            <div className="absolute w-full h-full backface-hidden [backface-visibility:hidden] [transform:rotateY(180deg)]">
                <div className="w-full h-full bg-brand-blue rounded-3xl shadow-xl flex flex-col items-center justify-center p-8 text-center text-white">
                    <span className="text-sm font-bold text-indigo-200 uppercase tracking-widest mb-4">Definition</span>
                    <h3 className="text-2xl font-medium">{currentCard.back}</h3>
                </div>
            </div>

        </div>
      </div>

      <div className="flex justify-center gap-4 mt-12">
        <Button variant="secondary" onClick={handlePrev} disabled={currentIndex === 0}>
            <i className="fas fa-arrow-left"></i> Prev
        </Button>
        <Button variant="outline" onClick={() => setIsFlipped(!isFlipped)}>
            Flip Card
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={currentIndex === cards.length - 1}>
            Next <i className="fas fa-arrow-right"></i>
        </Button>
      </div>

    </div>
  );
};

export default FlashcardsPage;
